import { useCallback, useEffect, useRef, useState } from 'react';
import { ComparablePlanType } from '@coverright/data-access/types/medicare';
import { useAppContext } from '@coverright/shared/contexts';
import { CartItem, EMPTY_CART, PlanCart } from './plan-selection-types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ConflictInfo {
  message: string;
  pendingItems: CartItem[];
}

export interface PlanSelectionHook {
  cart: PlanCart;
  checkoutOpen: boolean;
  checkoutStep: 1 | 2;
  conflictInfo: ConflictInfo | null;
  selectPlan: (items: CartItem | CartItem[]) => void;
  removePlanFromCart: (type: ComparablePlanType) => void;
  openCheckout: () => void;
  closeCheckout: () => void;
  setCheckoutStep: (step: 1 | 2) => void;
  confirmConflict: () => void;
  cancelConflict: () => void;
}

// ─── Cart ↔ AppState helpers ──────────────────────────────────────────────────

/**
 * Reconstruct a PlanCart from `appState.selectedPlans`.
 *
 * We store full CartItem display fields alongside the enrollment fields so
 * the cart can be fully restored without extra API calls.
 */
function cartFromAppState(appState: Record<string, any>): PlanCart {
  const plans: any[] = appState?.selectedPlans ?? [];
  const cart: PlanCart = { ...EMPTY_CART };

  for (const p of plans) {
    const item: CartItem = {
      type:       p.type,
      bidId:      p.bidId,
      zip:        p.zip        ?? '',
      county:     p.county     ?? '',
      planYear:   p.planYear,
      planName:   p.planName   ?? '',
      orgName:    p.orgName    ?? '',
      premium:    p.premium    ?? 0,
      deductible: p.deductible,
      moop:       p.moop,
      logoUrl:    p.logoUrl,
    };
    if (p.type === ComparablePlanType.Ma)  cart.ma  = item;
    if (p.type === ComparablePlanType.Mg)  cart.mg  = item;
    if (p.type === ComparablePlanType.Pdp) cart.pdp = item;
  }

  return cart;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePlanSelection(): PlanSelectionHook {
  const [appState, saveAppState, loaded] = useAppContext();

  // Ref keeps the latest cart value accessible synchronously (avoids stale closures)
  const cartRef = useRef<PlanCart>(EMPTY_CART);
  const [cart, _setCart] = useState<PlanCart>(EMPTY_CART);
  const setCart = useCallback((next: PlanCart) => {
    cartRef.current = next;
    _setCart(next);
  }, []);

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<1 | 2>(1);
  const [conflictInfo, setConflictInfo] = useState<ConflictInfo | null>(null);

  // ── Restore cart once AppContext finishes loading ────────────────────────────
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!loaded || restoredRef.current) return;
    restoredRef.current = true;

    const restored = cartFromAppState(appState as Record<string, any>);
    if (restored.ma || restored.mg || restored.pdp) {
      setCart(restored);
    }
  }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist to AppState ──────────────────────────────────────────────────────
  // We include display fields (planName, orgName, premium, etc.) in each
  // selectedPlan entry so they survive navigation and can be restored above.
  const persistCart = useCallback((c: PlanCart) => {
    const selectedPlans = [c.ma, c.mg, c.pdp]
      .filter((item): item is CartItem => item !== null)
      .map(item => ({
        // Enrollment fields (expected by server)
        bidId:    item.bidId,
        zip:      item.zip,
        county:   item.county,
        type:     item.type,
        planYear: item.planYear as any,
        // Display fields (stored for UI restoration)
        planName:   item.planName,
        orgName:    item.orgName,
        premium:    item.premium,
        deductible: item.deductible,
        moop:       item.moop,
        logoUrl:    item.logoUrl,
      }));
    saveAppState({ selectedPlans } as any);
  }, [saveAppState]);

  // ── Apply items to a base cart and open checkout ────────────────────────────
  const applyAndOpen = useCallback((items: CartItem[], base: PlanCart) => {
    const next = { ...base };
    for (const item of items) {
      if (item.type === ComparablePlanType.Ma)  next.ma  = item;
      if (item.type === ComparablePlanType.Mg)  next.mg  = item;
      if (item.type === ComparablePlanType.Pdp) next.pdp = item;
    }
    setCart(next);
    persistCart(next);
    setCheckoutStep(1);
    setCheckoutOpen(true);
  }, [setCart, persistCart]);

  // ── Select plan (entry point from cards) ────────────────────────────────────
  const selectPlan = useCallback((items: CartItem | CartItem[]) => {
    const toAdd = Array.isArray(items) ? items : [items];
    const current = cartRef.current;

    const isAddingMA      = toAdd.some(i => i.type === ComparablePlanType.Ma);
    const isAddingMGorPDP = toAdd.some(i => i.type !== ComparablePlanType.Ma);
    const hasMA           = current.ma  !== null;
    const hasMGorPDP      = current.mg  !== null || current.pdp !== null;

    // MA conflicts with existing MG / PDP
    if (isAddingMA && hasMGorPDP) {
      const existing = [
        current.mg  && 'Medicare Supplement plan',
        current.pdp && 'Drug plan',
      ].filter(Boolean).join(' and ');
      setConflictInfo({
        message: `You currently have a ${existing} selected. To add a Medicare Advantage plan, you must remove your Medicare Supplement plan and Drug plan. Continue?`,
        pendingItems: toAdd,
      });
      return;
    }

    // MG / PDP conflicts with existing MA
    if (isAddingMGorPDP && hasMA) {
      const addingNames = toAdd
        .filter(i => i.type !== ComparablePlanType.Ma)
        .map(i => i.type === ComparablePlanType.Mg ? 'Medicare Supplement plan' : 'Drug plan')
        .join(' and ');
      setConflictInfo({
        message: `You currently have a Medicare Advantage plan selected. To add a ${addingNames}, you must remove your Medicare Advantage plan. Continue?`,
        pendingItems: toAdd,
      });
      return;
    }

    // No conflict — apply directly
    applyAndOpen(toAdd, current);
  }, [applyAndOpen]);

  // ── Conflict resolution ──────────────────────────────────────────────────────
  const confirmConflict = useCallback(() => {
    if (!conflictInfo) return;
    const { pendingItems } = conflictInfo;
    const isAddingMA = pendingItems.some(i => i.type === ComparablePlanType.Ma);
    const cleared: PlanCart = isAddingMA
      ? EMPTY_CART
      : { ...cartRef.current, ma: null };
    applyAndOpen(pendingItems, cleared);
    setConflictInfo(null);
  }, [conflictInfo, applyAndOpen]);

  const cancelConflict = useCallback(() => setConflictInfo(null), []);

  // ── Remove a plan from cart ─────────────────────────────────────────────────
  const removePlanFromCart = useCallback((type: ComparablePlanType) => {
    const next = { ...cartRef.current };
    if (type === ComparablePlanType.Ma)  next.ma  = null;
    if (type === ComparablePlanType.Mg)  next.mg  = null;
    if (type === ComparablePlanType.Pdp) next.pdp = null;
    setCart(next);
    persistCart(next);
  }, [setCart, persistCart]);

  const closeCheckout = useCallback(() => setCheckoutOpen(false), []);
  const openCheckout  = useCallback(() => {
    setCheckoutStep(1);
    setCheckoutOpen(true);
  }, []);

  return {
    cart,
    checkoutOpen,
    checkoutStep,
    conflictInfo,
    selectPlan,
    removePlanFromCart,
    openCheckout,
    closeCheckout,
    setCheckoutStep,
    confirmConflict,
    cancelConflict,
  };
}
